import linkifyStr from 'linkify-string';
import DOMPurify from 'dompurify';
import Vue from 'vue';

const AUTOLINK_MARKER = 'data-autolinkified';
const URL_DISPLAY_MAX_LENGTH = 80;

const linkifyOptions = {
  defaultProtocol: 'https',
  target: '_blank',
  rel: 'noopener noreferrer',
  validate: {
    email: true,
  },
};

function autolinkify(value) {
  if (value === null || value === undefined || value === '') return '';

  const text = typeof value === 'string' ? value : String(value);
  const linkified = linkifyStr(text, linkifyOptions);

  const sanitized = DOMPurify.sanitize(linkified, {
    ALLOWED_TAGS: ['a', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
  });

  const temp = document.createElement('div');
  temp.innerHTML = sanitized;
  temp.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute(AUTOLINK_MARKER, 'true');
  });
  return temp.innerHTML;
}

function truncateForDisplay(href) {
  if (href.length <= URL_DISPLAY_MAX_LENGTH) return href;
  return href.slice(0, URL_DISPLAY_MAX_LENGTH - 1) + '…';
}

function handleAnchorClick(event) {
  if (event.defaultPrevented) return;

  const anchor = event.target.closest(`a[${AUTOLINK_MARKER}]`);
  if (!anchor) return;

  // Modifier-clicks already signal "open in new tab/window" — let the
  // browser handle them without a confirmation prompt.
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey) return;

  const href = anchor.getAttribute('href') || '';

  // mailto: opens the user's mail client — they're not "leaving the site"
  // in a way that needs a warning.
  if (href.startsWith('mailto:')) return;

  event.preventDefault();

  const message = `You're about to open an external link:\n\n${truncateForDisplay(href)}\n\nContinue?`;
  const openLink = () => window.open(href, '_blank', 'noopener,noreferrer');

  const root = typeof window !== 'undefined' ? window.$nuxt : null;
  if (!root) {
    openLink();
    return;
  }

  root.$emit('confirm', {
    message,
    yesButtonLabel: 'Open link',
    callback: openLink,
  });
}

function handle(el, binding) {
  el.innerHTML = autolinkify(binding?.value);
}

Vue.directive('autolinkify', {
  bind(el, binding) {
    handle(el, binding);
    el.addEventListener('click', handleAnchorClick);
  },
  update(el, binding) {
    handle(el, binding);
  },
  unbind(el) {
    el.removeEventListener('click', handleAnchorClick);
  },
});

export { autolinkify };
